/**
 * useZoomEffect — applies menu + content zoom CSS to the layout.
 *
 * Reads `#menu-zoom` and `#content-zoom` from Dexie settings and sets
 * CSS custom properties on the document root so sidebar and content areas
 * scale accordingly.
 *
 * CSS custom properties set:
 *   --menu-zoom: 1.0  (default)
 *   --content-zoom: 1.0  (default)
 *
 * These are consumed by the AppLayout to apply `zoom` on the sidebar
 * and main content containers.
 *
 * Excluded from zoom: FAB, Action Panel, Toast, Modals (overlays)
 * → those live outside the zoomed containers.
 *
 * @see Fas 35 in view-and-dnd-tasks.md
 */

import { useEffect } from "react"
import { useSetting } from "@/features/settings/hooks/useSetting"

export function useZoomEffect(): {
  menuZoom: number
  contentZoom: number
} {
  const { value: menuZoom } = useSetting<number>("#menu-zoom", 100)
  const { value: contentZoom } = useSetting<number>("#content-zoom", 100)

  useEffect(() => {
    const root = document.documentElement
    root.style.setProperty("--menu-zoom", String(menuZoom / 100))
    root.style.setProperty("--content-zoom", String(contentZoom / 100))

    return () => {
      root.style.removeProperty("--menu-zoom")
      root.style.removeProperty("--content-zoom")
    }
  }, [menuZoom, contentZoom])

  return { menuZoom, contentZoom }
}
